import { memo, type ReactNode, useCallback } from 'react';

import { type AirActionTrackingLocation } from '@air/analytics';
import { useAirModal } from '@air/provider-modal';

import { NoPermissionToCreateShareLink } from '~/components/NoPermissionToCreateShareLink';
import { EditableShareLink } from '~/components/ShareModal/components/ShareLink/EditableShareLink';
import { ShareLinkModalFooter } from '~/components/ShareModal/components/ShareLinkModalFooter';
import { ShareLinkModal } from '~/components/ShareModal/ShareLinkModal';
import { SharePrivateAssetModalBody } from '~/components/SharePrivateAssetModal/SharePrivateAssetModalBody';
import {
  getAssetShareLinkModalTitle,
  getAssetShareLinkObjectId,
  getAssetShareLinkOptionSelection,
  hasAnyEnabledAssetShareLinkOption,
} from '~/components/SharePrivateClipModal/buildAssetShareLinkSlots';
import { useAssetShareLinkSlotSelection } from '~/components/SharePrivateClipModal/hooks/useAssetShareLinkSlotSelection';
import { useEnsureAssetScopedShareLinks } from '~/components/SharePrivateClipModal/hooks/useEnsureAssetScopedShareLinks';
import { ReadonlyAssetSharePrivateLink } from '~/components/SharePrivateClipModal/ReadonlyAssetSharePrivateLink';
import { SharePrivateClipModal } from '~/components/SharePrivateClipModal/SharePrivateClipModal';
import { SharePrivateClipRegenerate } from '~/components/SharePrivateClipModal/SharePrivateClipRegenerate';
import { type AssetShareLinkModalClip } from '~/components/SharePrivateClipModal/types';
import { SharePrivateContentSpinner } from '~/components/SharePrivateContentModal/SharePrivateContentSpinner';
import { SharePrivateCreateLinkError } from '~/components/SharePrivateContentModal/SharePrivateCreateLinkError';
import { SHARE_PRIVATE_CONTENT_MODAL } from '~/constants/testIDs';
import { useShowTurnOnShareLinkNotifications } from '~/hooks/turnOnEmailShareNotificationsToast/useShowTurnOnShareLinkNotifications';
import { useClipPermissions } from '~/hooks/useClipPermissions';
import { type PrivateAssetOpenLocation } from '~/hooks/usePrivateAssetOpenLocation';
import {
  canChangeAssetCustomFields,
  canEditAssetDiscussions,
  canExportAsset,
  canSeeAssetCustomFields,
  canSeeAssetVersions,
  canViewAssetDiscussions,
} from '~/utils/permissions/assetPermissions';
import { canCreateShortUrl, canEditShortUrl, canViewShortUrl } from '~/utils/permissions/shortUrlPermissions';

export type SharePrivateAssetModalProps = {
  analyticsLocation: PrivateAssetOpenLocation | null;
  onClose: () => void;
  clip: AssetShareLinkModalClip;
  trackLocation: AirActionTrackingLocation;
};

export const SharePrivateAssetModal = memo((props: SharePrivateAssetModalProps) => {
  const { analyticsLocation, onClose, clip, trackLocation } = props;
  const assetId = clip.assetId;
  const { clipPermissions } = useClipPermissions({
    clipId: clip.id,
  });
  const canEdit = canEditShortUrl(clipPermissions);
  const canCreate = canCreateShortUrl(clipPermissions);
  const canViewCustomFields = canSeeAssetCustomFields(clipPermissions);
  const canEditCustomFields = canChangeAssetCustomFields(clipPermissions);
  const canManageDiscussions = canEditAssetDiscussions(clipPermissions);
  const canViewDiscussions = canViewAssetDiscussions(clipPermissions);
  const workspaceCanDownload = canExportAsset(clipPermissions);
  const canViewAssetVersions = clipPermissions ? canSeeAssetVersions(clipPermissions) : undefined;

  const {
    assetShareLinksItems,
    hasAssetShareLinksListError,
    hasResolvedAssetShareLinksList,
    isFetchingShareLinkOptions,
    isLoading: isShareLinksLoading,
    isRetryingAssetShareLinksList,
    onSelectedOptionChange,
    options,
    retryAssetShareLinksList,
    selectedOption,
    selectionContext,
  } = useAssetShareLinkSlotSelection({
    canCreateShareLink: clipPermissions ? canCreate : undefined,
    canViewAssetVersions,
    clip,
    // The asset modal and the agent panel both hand over the clip the link already points at.
    // Every other location should land on the first enabled link.
    preferEnabledShareLink: trackLocation !== 'asset-details' && trackLocation !== 'workspace-agent',
  });

  const { selectedSlotId, selectedSlotShortUrl } = getAssetShareLinkOptionSelection(selectedOption);
  const shortIdInfo = selectedSlotShortUrl?.id ? selectedSlotShortUrl : undefined;

  const { hasCreateError, hasCreatedShareLinkInThisSession, isCreatingShareLink, retryCreateShareLink } =
    useEnsureAssetScopedShareLinks({
      assetId,
      assetShareLinksItems,
      analyticsLocation,
      canCreate,
      hasResolvedAssetShareLinksList,
    });

  const [showRegenerateModal] = useAirModal(SharePrivateClipRegenerate);
  const [showShareModal, hideShareModal] = useAirModal(SharePrivateClipModal);
  const { showTurnOnShareLinkNotifications } = useShowTurnOnShareLinkNotifications();

  const hasAnyAssetShareLinks = assetShareLinksItems.length > 0;
  const modalTitle = getAssetShareLinkModalTitle(selectedSlotId);

  const handleRegenerate = useCallback(() => {
    if (!shortIdInfo) {
      return;
    }

    const selectedShareLinkBoardContext = shortIdInfo.boardContext ?? undefined;

    hideShareModal();

    const reopenShareModal = () => {
      showShareModal({
        analyticsLocation,
        clip,
        trackLocation,
        boardContext: selectedShareLinkBoardContext,
      });
    };

    showRegenerateModal({
      assetId,
      trackLocation,
      shortIdInfo,
      boardContext: selectedShareLinkBoardContext,
      onRegenerated: () => {
        reopenShareModal();
        showTurnOnShareLinkNotifications({
          onManageClick: hideShareModal,
        });
      },
      onCancel: reopenShareModal,
    });
  }, [
    assetId,
    analyticsLocation,
    shortIdInfo,
    hideShareModal,
    showRegenerateModal,
    clip,
    trackLocation,
    showShareModal,
    showTurnOnShareLinkNotifications,
  ]);

  let content: ReactNode;

  if (
    !clipPermissions ||
    isShareLinksLoading ||
    (!hasAnyAssetShareLinks &&
      (isCreatingShareLink || (hasCreatedShareLinkInThisSession && isFetchingShareLinkOptions)))
  ) {
    content = <SharePrivateContentSpinner />;
  } else if (hasAssetShareLinksListError) {
    content = (
      <SharePrivateCreateLinkError
        isRetrying={isRetryingAssetShareLinksList}
        message="Something went wrong. We couldn't load share links."
        onRetry={retryAssetShareLinksList}
      />
    );
  } else if (!hasAnyAssetShareLinks && hasCreateError && canCreate) {
    content = <SharePrivateCreateLinkError isRetrying={isCreatingShareLink} onRetry={retryCreateShareLink} />;
  } else if (canEdit) {
    content = (
      <SharePrivateAssetModalBody
        assetId={assetId}
        assetShareLinksItems={assetShareLinksItems}
        analyticsLocation={analyticsLocation}
        canCreateShareLink={canCreate}
        canEditCustomFields={canEditCustomFields}
        canManageDiscussions={canManageDiscussions}
        canViewAssetVersions={canViewAssetVersions === true}
        canViewCustomFields={canViewCustomFields}
        canViewDiscussions={canViewDiscussions}
        isFetchingShareLinkOptions={isFetchingShareLinkOptions}
        onSelectedOptionChange={onSelectedOptionChange}
        options={options}
        selectedOption={selectedOption}
        selectionContext={selectionContext}
        workspaceCanDownload={workspaceCanDownload}
      />
    );
  } else if (!canViewShortUrl(clipPermissions) || !hasAnyEnabledAssetShareLinkOption(options)) {
    content = <NoPermissionToCreateShareLink />;
  } else {
    content = (
      <ReadonlyAssetSharePrivateLink
        isFetchingShareLinkOptions={isFetchingShareLinkOptions}
        onSelectedOptionChange={onSelectedOptionChange}
        options={options}
        selectedOption={selectedOption}
        trackLocation={trackLocation}
      />
    );
  }

  const footer =
    shortIdInfo && canEdit ? (
      <ShareLinkModalFooter>
        <EditableShareLink
          createdAt={shortIdInfo.createdAt}
          createdBy={shortIdInfo.createdBy}
          objectId={getAssetShareLinkObjectId(shortIdInfo, assetId)}
          objectType={shortIdInfo.objectType}
          shareId={shortIdInfo.id}
          shortIdInfo={shortIdInfo}
          trackLocation={trackLocation}
          onRegenerate={handleRegenerate}
        />
      </ShareLinkModalFooter>
    ) : undefined;

  return (
    <ShareLinkModal data-testid={SHARE_PRIVATE_CONTENT_MODAL} title={modalTitle} onClose={onClose}>
      <div className="mt-3 flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto border-t ads:border-t-default border-t-gray-4 px-6 pt-4 pb-4">
        {content}
      </div>
      {footer}
    </ShareLinkModal>
  );
});

SharePrivateAssetModal.displayName = 'SharePrivateAssetModal';
